import type { Rng } from '../rng';
import type { IncidentKind } from '../mission/incident';

/**
 * Die Spielfigur.
 *
 * Das Aussehen ist die Kennung eines fertigen Portraets, nicht mehr eine
 * Sammlung von Reglerwerten. Der Grund ist schlicht: Sechs Regler auf
 * gezeichneten Ebenen sahen nach Platzhalter aus, und das Erste, was man vom
 * Spiel sieht, sollte nicht nach Platzhalter aussehen. Die Kennung bleibt
 * reine Zeichenkette — `core/` weiss nichts von Bilddateien.
 *
 * Der Hintergrund ist dagegen kein Beiwerk: Jeder der drei wirkt auf ein
 * anderes bestehendes System, und jeder bringt einen Verlust mit, der im
 * Finale wieder auftaucht.
 */

export type Background = 'pilot' | 'naval_officer' | 'marine_biologist';

export const BACKGROUNDS: readonly Background[] = ['pilot', 'naval_officer', 'marine_biologist'];

/**
 * Die waehlbaren Portraets.
 *
 * Die Reihenfolge ist die des Auswahlrasters und Teil des Speicherformats —
 * neue Portraets gehoeren ans Ende, damit bestehende Staende gueltig bleiben.
 * Jede Kennung muss eine Datei unter `public/assets/portraits/` haben;
 * tests/assets.test.ts haelt das fest.
 */
export const PORTRAIT_IDS: readonly string[] = [
  'p01', 'p02', 'p03', 'p04', 'p05',
  'p06', 'p07', 'p08', 'p09', 'p10',
  'p11', 'p12', 'p13', 'p14', 'p15',
];

/** Das Portraet der Institutsleitung. Bewusst nicht waehlbar. */
export const ADVISER_PORTRAIT_ID = 'roskopf';

export interface Character {
  readonly name: string;
  /** Kennung aus {@link PORTRAIT_IDS}. */
  readonly portraitId: string;
  readonly background: Background;
  /** Name der Person, die verloren ging. */
  readonly lostName: string;
  /** Uebersetzungsschluessel des Verwandtschafts- oder Dienstverhaeltnisses. */
  readonly lostRelationKey: string;
}

/**
 * Was ein Hintergrund kann.
 *
 * Bewusst je genau eine Wirkung, und jede auf ein System, das es schon gibt.
 * Drei kleine, klar unterscheidbare Vorteile sind lesbarer als neun
 * verrechnete Prozentwerte.
 */
export interface BackgroundEffects {
  /**
   * Faktor auf die Unsicherheit der Prognose. Kleiner heisst sicherer.
   * Der Pilot liest Absturzmuster, die andere raten muessen.
   */
  forecastUncertaintyFactor(kind: IncidentKind): number;
  /** Faktor auf die Rumpfbelastung. Der Marineoffizier faehrt die See besser ab. */
  readonly hullStressFactor: number;
  /**
   * Zusaetzliche Hinweise bei kalten Spuren. Die Meeresbiologin liest die
   * Umgebung, wo andere nur leeres Wasser sehen.
   */
  readonly coldTrailBonusClues: number;
}

const AIRBORNE_KINDS: readonly IncidentKind[] = ['airliner', 'light_aircraft'];

const EFFECTS: Readonly<Record<Background, BackgroundEffects>> = {
  pilot: {
    // Bei Luftfahrzeugen weiss der Pilot, was ein Aufschlag anrichtet und wie
    // lange eine Rettungsinsel haelt — die Schaetzung wird deutlich schaerfer.
    forecastUncertaintyFactor: (kind) => (AIRBORNE_KINDS.includes(kind) ? 0.35 : 1),
    hullStressFactor: 1,
    coldTrailBonusClues: 0,
  },
  naval_officer: {
    forecastUncertaintyFactor: () => 1,
    // Wer jahrelang schwere See gefahren ist, schont den Rumpf.
    hullStressFactor: 0.6,
    coldTrailBonusClues: 0,
  },
  marine_biologist: {
    forecastUncertaintyFactor: () => 1,
    hullStressFactor: 1,
    // Wo nichts mehr treibt, bleibt immer noch das Wasser selbst.
    coldTrailBonusClues: 1,
  },
};

export function backgroundEffects(background: Background): BackgroundEffects {
  return EFFECTS[background];
}

/**
 * Wen die Figur verloren hat — je Hintergrund ein anderes Verhaeltnis.
 * Der Verlust ist der Grund, warum sie hier draussen ist.
 */
export const LOST_RELATION_KEY: Readonly<Record<Background, string>> = {
  pilot: 'background.lost.wingman',
  naval_officer: 'background.lost.crew',
  marine_biologist: 'background.lost.parents',
};

const GIVEN_NAMES = [
  'Ellis', 'Marek', 'Salome', 'Idris', 'Junot', 'Nadia', 'Cormac', 'Thea',
  'Ivo', 'Ruth', 'Anselm', 'Vera', 'Kai', 'Miriam', 'Osian', 'Lene',
] as const;

const SURNAMES = [
  'Vandermeer', 'Okonkwo', 'Ferreira', 'Lindqvist', 'Amari', 'Brennan',
  'Kowalczyk', 'Halloran', 'Duarte', 'Nakamura', 'Sorensen', 'Achterberg',
] as const;

export function randomName(rng: Rng): string {
  return `${rng.pick(GIVEN_NAMES)} ${rng.pick(SURNAMES)}`;
}

export function randomPortraitId(rng: Rng): string {
  return rng.pick(PORTRAIT_IDS);
}

export function createCharacter(rng: Rng, overrides: Partial<Character> = {}): Character {
  const background = overrides.background ?? rng.pick(BACKGROUNDS);
  return {
    name: overrides.name ?? randomName(rng),
    portraitId: overrides.portraitId ?? randomPortraitId(rng),
    background,
    lostName: overrides.lostName ?? randomName(rng.derive('lost')),
    lostRelationKey: LOST_RELATION_KEY[background],
  };
}

/** Blaettert im Auswahlraster weiter, mit Umlauf. */
export function cyclePortrait(portraitId: string, delta: number): string {
  const at = PORTRAIT_IDS.indexOf(portraitId);
  const count = PORTRAIT_IDS.length;
  const next = (((at < 0 ? 0 : at) + delta) % count + count) % count;
  return PORTRAIT_IDS[next] ?? PORTRAIT_IDS[0]!;
}
